import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { UserRole } from '../../types'

import { getDisplayName } from '../getDisplayName'

import { useMe } from '@hooks/user/me.hook'
import { useAppState } from '@hooks/app/app-state.hook'

import { Loader } from '@components/Loader'
import { Empty } from '@components/Refresh/Empty'

const EmptyContainer = styled.div`
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;
`

interface Props {
  children: React.ReactNode
  hideMessage?: boolean
}

export const OrganizationGuard = (props: Props): JSX.Element => {
  const { organization, setOrganization } = useAppState()
  const { getMe } = useMe()

  const user = getMe()

  const { children, hideMessage } = props

  const isAdmin = () => user.role == UserRole.RoleAdmin

  const { t } = useTranslation()

  useEffect(() => {
    if (organization === undefined) {
      const defaultOrganization = user.organizationACL[0]?.organization
      setOrganization(defaultOrganization ? defaultOrganization : null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (organization === undefined) {
    return <Loader />
  }

  if (organization === null) {
    return (
      <>
        {hideMessage ? null : (
          <EmptyContainer>
            <Empty
              small
              label={
                isAdmin() || user.organizationACL.length > 0
                  ? t('utils_guard_withOrganizationGuard_select_organization')
                  : t('utils_guard_withOrganizationGuard_no_organization')
              }
            />
          </EmptyContainer>
        )}
      </>
    )
  }

  return <>{children}</>
}

interface WithOrganizationGuardParams {
  hideMessage?: boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export const withOrganizationGuard =
  (params?: WithOrganizationGuardParams) =>
  <P extends object>(WrappedComponent: React.ComponentType<P>) => {
    const WithOrganizationGuard = (props: P) => {
      return (
        <OrganizationGuard {...params}>
          <WrappedComponent {...props} />
        </OrganizationGuard>
      )
    }

    WithOrganizationGuard.displayName = `WithOrganizationGuard(${getDisplayName(WrappedComponent)})`

    return WithOrganizationGuard
  }
